import { usePermissions } from '@/hooks/use-permissions'
import { getToken, decodeToken } from '@/lib/auth/token'
import { type User } from '@/models/domain/user'
import { useAuthStore } from '@/stores/auth-store'

function BoolStatus({
  value,
  trueText,
  falseText,
}: {
  value: boolean
  trueText: string
  falseText: string
}) {
  if (value) {
    return <>{trueText}</>
  }
  return <>{falseText}</>
}

function BoldStatus({
  value,
  trueText,
  falseText,
}: {
  value: boolean
  trueText: string
  falseText: string
}) {
  if (value) {
    return <span className="text-green-600 font-bold">{trueText}</span>
  }
  return <span className="text-red-600 font-bold">{falseText}</span>
}

function ClaimsSummary({ claims }: { claims: string[] | undefined }) {
  if (claims?.length) {
    return (
      <span className="text-green-600">
        {claims.length} claims: [{claims.join(', ')}]
      </span>
    )
  }
  return <span className="text-red-600">L No claims</span>
}

function PermissionChecks({ isSysOp, user }: { isSysOp: boolean; user: User | null }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold mb-4">Permission Checks</h2>
      <div className="space-y-2 text-sm">
        <div>
          <strong>isSysOp:</strong>{' '}
          <BoolStatus value={isSysOp} trueText=" true" falseText="L false" />
        </div>
        <div>
          <strong>user.sys_op:</strong>{' '}
          <BoolStatus value={!!user?.sys_op} trueText=" true" falseText="L false" />
        </div>
        <div>
          <strong>user.keycloak_claims:</strong> <ClaimsSummary claims={user?.keycloak_claims} />
        </div>
        <div>
          <strong>Has manage-users:</strong>{' '}
          <BoolStatus
            value={!!user?.keycloak_claims?.includes('manage-users')}
            trueText=" true"
            falseText="L false"
          />
        </div>
      </div>
    </div>
  )
}

function SidebarNavCheck({ user }: { user: User | null }) {
  const hasSysOp = !!user?.sys_op
  const hasManageUsers = !!user?.keycloak_claims?.includes('manage-users')

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h2 className="text-lg font-semibold mb-4">Sidebar Navigation Check</h2>
      <div className="space-y-2 text-sm">
        <div>
          <strong>Operations menu requires:</strong>
          <ul className="ml-4 mt-2 space-y-1">
            <li>
              {'\u201C'} requireSysOp: true {'\u2192'}{' '}
              <BoolStatus value={hasSysOp} trueText=" PASS" falseText="L FAIL" />
            </li>
            <li>
              {'\u201C'} keycloakClaims: [{'\u0027'}manage_users{'\u0027'}] {'\u2192'}{' '}
              <BoolStatus value={hasManageUsers} trueText=" PASS" falseText="L FAIL" />
            </li>
          </ul>
        </div>
        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded">
          <strong>Should Operations section be visible?</strong>{' '}
          <BoldStatus value={hasSysOp && hasManageUsers} trueText=" YES" falseText="L NO" />
        </div>
      </div>
    </div>
  )
}

export function DebugPage() {
  const { user } = useAuthStore()
  const { isSysOp } = usePermissions()
  const token = getToken()
  const claims = token ? decodeToken(token) : null

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Debug: Auth State</h1>

      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4">User Object (from Zustand)</h2>
          <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded text-xs overflow-auto">
            {JSON.stringify(user, null, 2)}
          </pre>
        </div>

        <PermissionChecks isSysOp={isSysOp} user={user} />

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4">Token Claims (Raw)</h2>
          <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded text-xs overflow-auto max-h-96">
            {JSON.stringify(claims, null, 2)}
          </pre>
        </div>

        <SidebarNavCheck user={user} />
      </div>
    </div>
  )
}
